// Core imports
import * as React from 'react';
import jwt_decode from 'jwt-decode';
import { enableScroll, disableScroll } from 'src/utils/documentInteraction';

// Components imports
import Button from 'src/components/common/Button';
import DateRangeSeparated from 'src/components/common/DateRangeSeparated';
import SortCard from 'src/components/common/SortCard';

// Models imports
import DateRangeModel from 'src/model/dateRange.model';
import {
  statusType,
  sortType,
  orderType,
  invoiceType,
  nardType,
  ordernumberType,
  poType
} from 'src/model/modules/invoices.model';
import {
  IFiltersInvoice,
  IPaginationInvoice
} from 'src/services/invoice/InvoiceService';

// Style and misc imports
import './InvoiceMobileFilters.scss';
import Select from 'src/components/common/Select';
import { Moment } from 'moment';

class InvoiceMobileFilters extends React.Component<
  IInvoiceMobileFiltersProps,
  IInvoiceMobileFiltersState
> {
  constructor(props: IInvoiceMobileFiltersProps) {
    super(props);
    this.state = {
      status: null,
      orderStatus: null,
      invoiceNumber: null,
      nardaNumber: null,
      orderNumber: null,
      orderReference: null,
      dates: null,
      sort: {
        sortBy: null,
        sortIn: 'asc',
        label: 'string'
      }
    };
  }

  /* 
    setStatus
   */
  private setStatus = (status: IStatusOption) => {
    this.setState({ status });
  };

  /* 
    setDates
   */
  private setDates = (dates: { start: Moment; end: Moment }) => {
    this.setState({ dates });
  };

  /* 
    setSort
   */
  private setSort = (sort: ISort) => {
    this.setState({ sort });
  };

  /* 
    getSorts
   */
  private getSorts = (t: any): JSX.Element[] => {
    const sortBy: ISort[] = [
      {
        sortBy: 'invoiceNumber',
        sortIn: 'asc',
        label: t('invoices.filters.invoiceNumberAsc')
      },
      {
        sortBy: 'invoiceNumber',
        sortIn: 'desc',
        label: t('invoices.filters.invoiceNumberDesc')
      },
      {
        sortBy: 'dueDate',
        sortIn: 'desc',
        label: t('invoices.filters.dueDateAsc')
      },
      {
        sortBy: 'dueDate',
        sortIn: 'asc',
        label: t('invoices.filters.dueDateDesc')
      },
      {
        sortBy: 'remainingAmount',
        sortIn: 'asc',
        label: t('invoices.filters.amountAsc')
      },
      {
        sortBy: 'remainingAmount',
        sortIn: 'desc',
        label: t('invoices.filters.amountDesc')
      },
      {
        sortBy: 'status',
        sortIn: 'asc',
        label: t('invoices.filters.statusAsc')
      },
      {
        sortBy: 'status',
        sortIn: 'desc',
        label: t('invoices.filters.statusDesc')
      }
    ];
    const sorts: JSX.Element[] = [];
    sortBy.map((sort: ISort, index) => {
      sorts.push(
        <SortCard
          key={index}
          sort={sort}
          setSort={this.setSort}
          active={
            this.state.sort.sortBy === sort.sortBy &&
            this.state.sort.sortIn === sort.sortIn
          }
        />
      );
    });

    return sorts;
  };

  /* 
    saveFilters
   */
  private saveFilters = () => {
    const {
      status,
      dates,
      sort,
      orderStatus,
      invoiceNumber,
      nardaNumber,
      orderNumber,
      orderReference
    } = this.state;
    const filters: IFiltersInvoice = {
      dateRange: dates ? dates : null,
      status: status ? status.value : null,
      orderStatus: orderStatus ? orderStatus.value : null,
      invoiceNumber: invoiceNumber ? invoiceNumber.value : null,
      nardaNumber: nardaNumber ? nardaNumber.value : null,
      orderNumber: orderNumber ? orderNumber.value : null,
      orderReference: orderReference ? orderReference.value : null,
      sort:
        sort && sort.sortBy && sort.sortIn
          ? { type: sort.sortBy, order: sort.sortIn }
          : null
    };
    const pagination: IPaginationInvoice = {
      page: 0
    };

    this.props.getInvoices(filters, pagination);
    this.props.closeFilterMenu();
  };

  private getStatuses() {
    const { t } = this.props;
    return [
      { value: 'dueToday', label: t('invoices.dueToday') },
      { value: 'paid', label: t('invoices.paid') },
      { value: 'pastDue', label: t('invoices.pastDue') },
      { value: 'pending', label: t('invoices.pending') },
      { value: 'open', label: t('invoices.open') },
      { value: 'all', label: t('common.all') }
    ];
  }

  public render() {
    const { t } = this.props;
    const statusOptions = this.getStatuses();
    return (
      <div className="rp-invoice-mobile-filters">
        <div className="rp-invoice-mobile-filters--content">
          <span className="content--filter-by">{t('common.filterBy')}</span>
          {/* Statuses */}
          <div className="content--statuses">
            <Select
              onSelected={option => {
                this.setStatus(option);
              }}
              options={statusOptions}
              optionsShown={statusOptions.length}
              activeOption={this.state.status}
              isLoading={false}
              placeholder={t('invoices.open')}
              placeholderTransform="capitalize"
              height="100%"
            />
          </div>

          {/* Dates */}
          <div className="content--calendars">
            <DateRangeSeparated
              dates={null}
              onDatesSelected={selectedDates => {
                this.setDates(selectedDates);
              }}
            />
          </div>

          {/* Sorts */}
          <div className="content--sort-by">
            <div className="sort-by--header">
              <span>{`${t('common.sortBy')}:`}</span>
              <i className="rpds-expand-more" />
            </div>
            <div className="sort-by--sorts">{this.getSorts(t)}</div>
          </div>
        </div>
        <Button
          buttonClass="primary-custom-button"
          label={t('common.saveFilters')}
          textTransform="uppercase"
          width="100%"
          height="48px"
          action={() => {
            this.saveFilters();
          }}
        />
      </div>
    );
  }
}

export default InvoiceMobileFilters;

interface IInvoiceMobileFiltersProps {
  t: any;
  getInvoices(filters: IFiltersInvoice, pagination: IPaginationInvoice): void;
  closeFilterMenu(): void;
}

interface IInvoiceMobileFiltersState {
  status: IStatusOption;
  orderStatus: IOrderStatusOption;
  invoiceNumber: IInvoiceOption;
  nardaNumber: INardOption;
  orderNumber: IOrderOption;
  orderReference: IPoOption;
  dates: DateRangeModel | null;
  sort: ISort;
}

interface ISort {
  sortBy: sortType;
  sortIn: 'asc' | 'desc';
  label: string;
}

interface IStatusOption {
  value: statusType;
  label: string;
}

interface IOrderStatusOption {
  value: orderType;
  label: string;
}
interface IInvoiceOption {
  value: invoiceType;
  label: string;
}
interface INardOption {
  value: nardType;
  label: string;
}
interface IOrderOption {
  value: ordernumberType;
  label: string;
}
interface IPoOption {
  value: poType;
  label: string;
}
